// Shared money / margin / "show the math" helpers for the Part, Assembly,
// Quote and Sales Order pages. One definition so every page formats and
// derives the same way. All money in/out as plain numbers; every helper
// tolerates null/undefined/NaN and never throws.

const moneyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const money4Fmt = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4,
});

export function num(v, fallback = 0) {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

/** $1,234.56. `dash` renders '—' for null/undefined/NaN instead of $0.00. */
export function fmtMoney(v, { dash = false, precise = false } = {}) {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return dash ? '—' : moneyFmt.format(0);
  const n = Number(v);
  return precise ? money4Fmt.format(n) : moneyFmt.format(n);
}

/** Signed delta: +$12.34 / −$0.50. */
export function fmtDelta(v) {
  const n = num(v);
  const s = moneyFmt.format(Math.abs(n));
  if (approxEq(n, 0)) return '±$0.00';
  return (n > 0 ? '+' : '−') + s;
}

/** 0.4 -> 40.0%  (fraction in). `digits` defaults to 1. */
export function fmtPct(frac, digits = 1, { dash = true } = {}) {
  if (frac === null || frac === undefined || Number.isNaN(Number(frac))) return dash ? '—' : '0%';
  return `${(Number(frac) * 100).toFixed(digits)}%`;
}

/** $130.00/hr */
export function fmtRate(perHour, { dash = true } = {}) {
  if (perHour === null || perHour === undefined || Number.isNaN(Number(perHour))) return dash ? '—' : '$0.00/hr';
  return `${moneyFmt.format(Number(perHour))}/hr`;
}

/** 25 -> "25.0 min", 0.5 -> "0.50 min" */
export function fmtMinutes(min) {
  if (min === null || min === undefined || Number.isNaN(Number(min))) return '—';
  const n = Number(min);
  return n >= 10 ? `${n.toFixed(1)} min` : `${n.toFixed(2)} min`;
}

export function fmtHours(h) {
  if (h === null || h === undefined || Number.isNaN(Number(h))) return '—';
  return `${Number(h).toFixed(4)} h`;
}

/** Margin = (price − cost) / price, as a fraction; null when price is 0 or inputs missing. */
export function marginPct(cost, price) {
  const c = num(cost, NaN); const p = num(price, NaN);
  if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) return null;
  return (p - c) / p;
}

/** Markup = (price − cost) / cost, as a fraction; null when cost is 0 or inputs missing. */
export function markupPct(cost, price) {
  const c = num(cost, NaN); const p = num(price, NaN);
  if (!Number.isFinite(c) || !Number.isFinite(p) || c === 0) return null;
  return (p - c) / c;
}

/** price that yields margin m (fraction) on cost: cost / (1 − m). Returns null for m >= 1. */
export function priceFromMargin(cost, m) {
  const c = num(cost, NaN); const mm = num(m, NaN);
  if (!Number.isFinite(c) || !Number.isFinite(mm) || mm >= 1) return null;
  return c / (1 - mm);
}

/** price that yields markup k (fraction) on cost: cost × (1 + k). */
export function priceFromMarkup(cost, k) {
  const c = num(cost, NaN); const kk = num(k, NaN);
  if (!Number.isFinite(c) || !Number.isFinite(kk)) return null;
  return c * (1 + kk);
}

/** Two money amounts equal to the cent (or within `tol`). */
export function approxEq(a, b, tol = 0.005) {
  return Math.abs(num(a, NaN) - num(b, NaN)) <= tol;
}

/**
 * Build a human formula string from labelled terms, e.g.
 *   formulaString([['labor', 0.91], ['gas', 0.38], ['material', 39.92]], 41.21)
 *   -> "labor $0.91 + gas $0.38 + material $39.92 = $41.21"
 * Terms with `{ mul: 1.1 }` render as "(… ) × 1.10". Falsy/zero terms are kept
 * (the operator should see that a term IS zero) unless `dropZero` is set.
 */
export function formulaString(terms, total, { dropZero = false } = {}) {
  const parts = (terms || [])
    .filter(t => Array.isArray(t) && t.length >= 2)
    .filter(t => !dropZero || !approxEq(t[1], 0))
    .map(([label, value, opts]) => {
      const base = `${label} ${fmtMoney(value)}`;
      if (opts && opts.mul != null) return `(${base}) × ${Number(opts.mul).toFixed(2)}`;
      return base;
    });
  const lhs = parts.join(' + ');
  return total === undefined ? lhs : `${lhs} = ${fmtMoney(total)}`;
}

/** "as of 09-01 12:54" from an ISO timestamp (local time); '' when missing. */
export function asOf(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `as of ${mm}-${dd} ${hh}:${mi}`;
}

/** Bootstrap badge class for a provenance/source tag. */
export function sourceBadgeClass(source) {
  switch (source) {
    case 'historical': return 'badge bg-info text-dark';
    case 'estimate': return 'badge bg-secondary';
    case 'assembly_row': return 'badge bg-primary';
    case 'vendor': return 'badge bg-dark';
    case 'default': return 'badge bg-light text-dark border';
    case 'stale': return 'badge bg-warning text-dark';
    case 'below_cost': return 'badge bg-danger';
    case 'override': return 'badge bg-warning text-dark';
    default: return 'badge bg-light text-dark border';
  }
}

/** Human label for a quote line price_source. */
export function priceSourceLabel(source) {
  switch (source) {
    case 'calculated': return 'Calculated';
    case 'target_margin': return 'Target margin';
    case 'book': return 'Book price';
    case 'manual': return 'Manual';
    case 'markup': return 'Cost + markup';
    case 'override_member': return 'In override';
    case 'ops': return 'Assembly ops';
    case 'quote_line': return 'Quote line';
    case 'ops_fold': return 'Assembly ops folded';
    case 'assembly_override': return 'Assembly override';
    case 'catalog': return 'Catalog price';
    // Time & Materials (sales_order_lines / quote_parts.price_source; jobq-api tm_pricing.TM_PRICE_SOURCES)
    case 'tm_pending': return 'T&M pending';
    case 'tm_manual': return 'T&M manual';
    case 'tm_actuals': return 'T&M actuals';
    default: return source ? String(source) : 'Unknown';
  }
}
